/**
 * The wire shapes `Rws.AiInsights.Host.Settings.PortalSettingsAdminEndpoints` returns (phase 21,
 * `AIA-81`): the vendor-write safety controls (`TD-22`, `AGENTS.md`'s blast-radius constraint) and
 * the dormancy threshold (`PD-13`). Both are gated on `settings.write`. No caching is added here on
 * top of the endpoint's own no-cache read — "the time to stop writes must be seconds".
 */
import { adminRequest, type AdminResult } from "./adminApi";

/** The vendor-write safety switches phase 18's reconciler decorator reads before every write. */
export interface WriteSafetySetting {
  changeBound: number;
  killSwitchEnabled: boolean;
  updatedAt: string;
}

/** The dormancy threshold, in days. */
export interface DormancyThresholdSetting {
  thresholdDays: number;
}

const writeSafetyPath = "/api/entitlement/settings/write-safety";
const dormancyThresholdPath = "/api/entitlement/settings/dormancy-threshold";

/** Calls `GET /api/entitlement/settings/write-safety`. */
export async function fetchWriteSafetySetting(): Promise<AdminResult<WriteSafetySetting>> {
  return adminRequest<WriteSafetySetting>(writeSafetyPath, { method: "GET" }, "write-safety setting");
}

/** Calls `PUT /api/entitlement/settings/write-safety`. */
export async function updateWriteSafetySetting(changeBound: number, killSwitchEnabled: boolean): Promise<AdminResult<WriteSafetySetting>> {
  return adminRequest<WriteSafetySetting>(
    writeSafetyPath,
    { method: "PUT", body: JSON.stringify({ changeBound, killSwitchEnabled }) },
    "write-safety update",
  );
}

/** Calls `GET /api/entitlement/settings/dormancy-threshold`. */
export async function fetchDormancyThresholdSetting(): Promise<AdminResult<DormancyThresholdSetting>> {
  return adminRequest<DormancyThresholdSetting>(dormancyThresholdPath, { method: "GET" }, "dormancy threshold");
}

/** Calls `PUT /api/entitlement/settings/dormancy-threshold`. */
export async function updateDormancyThresholdSetting(thresholdDays: number): Promise<AdminResult<DormancyThresholdSetting>> {
  return adminRequest<DormancyThresholdSetting>(
    dormancyThresholdPath,
    { method: "PUT", body: JSON.stringify({ thresholdDays }) },
    "dormancy threshold update",
  );
}
